import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Trash2, ListChecks, ChevronRight } from 'lucide-react';
import $ from 'jquery';
import axios from 'axios';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';
import 'datatables.net-responsive';

const SireInputAndList = ({ planState, setPlanState, setDialog, selectedFarm, onNext, nextLabel }) => {
    const tableRef = useRef(null);
    const dtApi = useRef(null);
    const [sireIdInput, setSireIdInput] = useState('');

    const handleAddAllInventorySires = useCallback(async () => {
        if (!selectedFarm?.id) {
            setDialog({ isOpen: true, title: "농장 미선택", message: "농장이 선택되지 않았습니다.", onClose: () => setDialog({ isOpen: false }) });
            return;
        }

        try {
            // 1. 보유품관리 씨수소 데이터 가져오기
            const response = await axios.get('/api/inventory', {
                params: { member_id: selectedFarm.id, nation: planState.filters?.country }
            });

            const inventorySires = response.data?.list || [];

            if (inventorySires.length === 0) {
                setDialog({ isOpen: true, title: "알림", message: "보유품관리에 등록된 씨수소가 없습니다.", onClose: () => setDialog({ isOpen: false }) });
                return;
            }

            // 2. 추가할 씨수소 필터링 (이미 목록에 있는 것 제외)
            const currentSireIds = new Set(planState.selectedSires.map(s => s.jm_semencode));
            const newSires = inventorySires.filter(s => !currentSireIds.has(s.jm_semencode));

            if (newSires.length === 0) {
                setDialog({ isOpen: true, title: "알림", message: "모든 보유 씨수소가 이미 목록에 추가되어 있습니다.", onClose: () => setDialog({ isOpen: false }) });
                return;
            }

            // 3. 10개 제한 확인
            const availableSlots = 10 - planState.selectedSires.length;
            if (availableSlots <= 0) {
                setDialog({ isOpen: true, title: "제한 초과", message: "씨수소는 최대 10개까지만 추가할 수 있습니다.", onClose: () => setDialog({ isOpen: false }) });
                return;
            }

            const siresToAdd = newSires.slice(0, availableSlots);
            const skippedCount = newSires.length - siresToAdd.length;

            if (newSires.length > 0) {
                setPlanState(prev => ({
                    ...prev,
                    selectedSires: [...prev.selectedSires, ...newSires]
                }));

                let message = `${newSires.length}마리의 씨수소가 추가되었습니다.`;
                if (skippedCount > 0) {
                    message += `\n(10개 제한으로 인해 ${skippedCount}마리는 추가되지 않았습니다.)`;
                }
                setDialog({ isOpen: true, title: "추가 완료", message: message, onClose: () => setDialog({ isOpen: false }) });
            } else {
                setDialog({ isOpen: true, title: "알림", message: "추가할 수 있는 유효한 씨수소 정보를 찾을 수 없습니다.", onClose: () => setDialog({ isOpen: false }) });
            }

        } catch (error) {
            console.error("보유 씨수소 가져오기 실패:", error);
            setDialog({ isOpen: true, title: "오류", message: "보유 씨수소 목록을 가져오는 중 오류가 발생했습니다.", onClose: () => setDialog({ isOpen: false }) });
        }
    }, [selectedFarm, planState.selectedSires, setPlanState, setDialog]);

    const handleAddSire = useCallback(async () => {
        const trimmedId = sireIdInput.trim().toUpperCase();
        if (!trimmedId) {
            setDialog({ isOpen: true, title: "입력 오류", message: "씨수소 번호를 입력해주세요.", onClose: () => setDialog({ isOpen: false }) });
            return;
        }

        // 씨수소 개수 제한
        if (planState.selectedSires.length >= 10) {
            setDialog({ isOpen: true, title: "제한 초과", message: "씨수소는 최대 10개까지 추가할 수 있습니다.", onClose: () => setDialog({ isOpen: false }) });
            return;
        }

        // 이미 목록에 있는지 확인
        if (planState.selectedSires.some(s => s.jm_semencode === trimmedId)) {
            setDialog({ isOpen: true, title: "중복", message: `씨수소 '${trimmedId}'는 이미 목록에 있습니다.`, onClose: () => setDialog({ isOpen: false }) });
            setSireIdInput('');
            return;
        }

        try {
            // API를 통해 씨수소 정보 조회
            const response = await axios.get(`/api/ocapa`, {
                params: { jm_semecode: trimmedId, nation: planState.filters?.country }
            });
            const sireToAdd = response.data;

            if (!sireToAdd || Object.keys(sireToAdd).length === 0) {
                setDialog({ isOpen: true, title: "미등록", message: `입력하신 씨수소 번호 '${trimmedId}'를 찾을 수 없습니다.`, onClose: () => setDialog({ isOpen: false }) });
                return;
            }

            // 상태 업데이트
            setPlanState(prev => ({
                ...prev,
                selectedSires: [...prev.selectedSires, sireToAdd.list[0]]
            }));

            setDialog({
                isOpen: true,
                title: "추가 완료",
                message: `씨수소 '${sireToAdd.list[0].jm_semencode}'가 목록에 추가되었습니다.`,
                onClose: () => setDialog({ isOpen: false })
            });
            setSireIdInput(''); // 입력창 초기화
        } catch (error) {
            console.error("씨수소 정보 조회 실패:", error);
            setDialog({ isOpen: true, title: "조회 실패", message: "씨수소 정보를 가져오는 중 오류가 발생했습니다.", onClose: () => setDialog({ isOpen: false }) });
        }
    }, [planState.selectedSires, setPlanState, setDialog, sireIdInput]);

    const handleDeleteSire = useCallback((sireId) => {
        setDialog({
            isOpen: true,
            title: "삭제 확인",
            message: `${sireId} 씨수소를 목록에서 삭제하시겠습니까?`,
            onConfirm: () => {
                setPlanState(prev => ({
                    ...prev,
                    selectedSires: prev.selectedSires.filter(s => s.jm_semencode !== sireId),
                }));
                setDialog({ isOpen: false });
            },
            onClose: () => setDialog({ isOpen: false }),
        });
    }, [setPlanState, setDialog]);

    const columns = useMemo(() => {
        const country = planState.filters?.country;
        // 1. 공통 컬럼 - 앞부분
        const baseColumns = [
            { title: '씨수소번호', data: 'jm_semencode' },
            { title: '명호(한)', data: 'h_short' },
            { title: '명호(영)', data: 'e_short' },
            { title: 'A2', data: 'beta_casein' },
        ];

        // 3. 공통 컬럼 - 뒷부분
        const tailColumns = [
            { title: '부X외조부', data: 'siremgsmggs' },
            { title: '삭제', data: null }
        ];

        // 2. 국가별 헤더 및 구성 정의
        let dynamicPart = [];

        if (country === '03') { // 캐나다
            dynamicPart = [
                { title: 'LPI', data: 'ocapa_tpi' },
                { title: 'Pro$', data: 'netmerit' },
                { title: 'Milk', data: 'pta_milk' },
                { title: 'Protein', data: 'pta_pro' },
                { title: 'Fat', data: 'pta_fat' },
                { title: 'Conformation', data: 'ptat' },
                { title: 'Mammary System', data: 'udc' },
                { title: 'Feet & Legs', data: 'flc' },
                { title: 'Herd Life', data: 'pl' },
                { title: 'Calving Ability', data: 'dpr' },
                { title: 'Daughter Calving Ability', data: 'nansan' },
                { title: 'Milking Speed', data: 'miking_speed' },
            ];
        } else if (country === '02' || country === '04') { // 미국, 기타
            dynamicPart = [
                { title: 'TPI', data: 'ocapa_tpi' },
                { title: 'NM$', data: 'netmerit' },
                { title: 'PTAM', data: 'pta_milk' },
                { title: 'PTAP', data: 'pta_pro' },
                { title: 'PTAF', data: 'pta_fat' },
                { title: 'PTAT', data: 'ptat' },
                { title: 'UDC', data: 'udc' },
                { title: 'FLC', data: 'flc' },
                { title: 'PL', data: 'pl' },
                { title: 'DPR', data: 'dpr' },
                { title: 'SCS', data: 'scs' },
                { title: 'Milking Speed', data: 'miking_speed' },
            ];
        } else { // 한국 (01) 및 기본값
            dynamicPart = [
                { title: 'KTPI', data: 'ocapa_tpi' },
                { title: 'MFP천원', data: 'netmerit' },
                { title: 'EBVM', data: 'pta_milk' },
                { title: 'EBVP', data: 'pta_pro' },
                { title: 'EBVF', data: 'pta_fat' },
                { title: 'PTAT', data: 'ptat' },
                { title: 'UDC', data: 'udc' },
                { title: 'FLC', data: 'flc' },
                { title: 'Herd Life', data: 'pl' },
                { title: 'SCS', data: 'scs' },
            ];
        }

        return [...baseColumns, ...dynamicPart, ...tailColumns];
    }, [planState.filters?.country]);
    const isInitialized = useRef(false);

    useEffect(() => {
        const options = {
            columns: columns,
            responsive: { details: { type: 'inline' } },
            paging: false,
            searching: false,
            ordering: true,
            info: false,
            language: { emptyTable: "선택된 씨수소가 없습니다." },
            columnDefs: [
                { responsivePriority: 2, targets: 0, className: 'dt-body-right' }, // '씨수소번호' 우측 정렬
                { targets: 1 }, // 명호(한)
                {
                    targets: columns.length - 1, // 삭제 버튼
                    responsivePriority: 1,
                    orderable: false,
                    className: 'dt-body-center',
                    render: (data, type, row) => `<button class="datatable-action-button danger-button" data-sire-id="${row.jm_semencode}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg></button>`
                }
            ],
            drawCallback: function () {
                this.api().responsive.recalc();
            }
        };

        // DataTables 초기화 전에 테이블 요소를 비웁니다.
        if (tableRef.current) {
            $(tableRef.current).empty();
        }

        // DataTables가 초기화되지 않았을 때만 새로 생성합니다.
        if (!isInitialized.current && tableRef.current) {
            dtApi.current = $(tableRef.current).DataTable(options);
            isInitialized.current = true;
        }

        const tableNode = tableRef.current;
        const clickHandler = function () {
            const sireId = $(this).data('sire-id');
            handleDeleteSire(sireId);
        };

        // 이벤트 핸들러를 한 번만 등록합니다.
        $(tableNode).on('click', '.datatable-action-button', clickHandler);

        return () => {
            // 컴포넌트 언마운트 시 이벤트 핸들러를 제거하고 DataTables 인스턴스를 파괴합니다.
            $(tableNode).off('click', '.datatable-action-button', clickHandler);
            if (dtApi.current && $.fn.DataTable.isDataTable(tableNode)) {
                dtApi.current.destroy();
                dtApi.current = null;
                isInitialized.current = false;
            }
        };
    }, [handleDeleteSire, columns]); // columns가 변경되면 테이블을 다시 초기화해야 합니다.

    useEffect(() => {
        // DataTables 인스턴스가 존재하고, 데이터가 변경되었을 때 테이블을 다시 그립니다.
        if (dtApi.current && isInitialized.current) {
            dtApi.current.clear().rows.add(planState.selectedSires).draw();
        }
    }, [planState.selectedSires]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="card">
                <h4 style={{ fontWeight: '600', marginBottom: '0.75rem' }}>씨수소 직접 추가</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="씨수소 번호 입력 (예: 551JE0205)"
                        value={sireIdInput}
                        onChange={(e) => setSireIdInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddSire()}
                    />
                    <button onClick={handleAddSire} className="action-button" style={{ flexShrink: 0 }}>
                        목록에 추가
                    </button>
                    <button onClick={handleAddAllInventorySires} className="secondary-button" style={{ flexShrink: 0 }}>
                        보유품 전체 추가
                    </button>
                </div>
            </div>

            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h4 style={{ display: 'flex', alignItems: 'center', fontWeight: '600', margin: 0 }}>
                        <ListChecks size={18} style={{ marginRight: '0.5rem' }} />
                        계획교배에 사용할 씨수소 목록 ({planState.selectedSires.length}마리)
                    </h4>
                    {onNext && (
                        <button onClick={onNext} className="action-button" style={{ padding: '0.45rem 1rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {nextLabel} <ChevronRight size={16} />
                        </button>
                    )}
                </div>
                <div className="table-container">
                    <table ref={tableRef} className="display nowrap" style={{ width: '100%' }}></table>
                </div>
            </div>
        </div>
    );
};

export default SireInputAndList;
